/**
 * Session discovery → réservation pour Matching Efficiency fondateurs.
 * search_id + top5_pro_ids en sessionStorage (fenêtre navigateur).
 */

const STORAGE_KEY = 'rt_discovery_matching_v1';

export type DiscoveryMatchingEntry = 'discovery' | 'direct' | 'ask';

export type DiscoveryMatchingState = {
  search_id: string;
  top5_pro_ids: number[];
  result_count: number;
  mode: 'search' | 'ask';
  updated_at: number;
};

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function newSearchId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `srch_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function readDiscoveryMatchingState(): DiscoveryMatchingState | null {
  if (!canUseStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DiscoveryMatchingState;
    if (!parsed || typeof parsed.search_id !== 'string' || !parsed.search_id) return null;
    return {
      search_id: parsed.search_id,
      top5_pro_ids: Array.isArray(parsed.top5_pro_ids)
        ? parsed.top5_pro_ids.map(Number).filter(n => Number.isFinite(n) && n > 0)
        : [],
      result_count: Number(parsed.result_count) || 0,
      mode: parsed.mode === 'ask' ? 'ask' : 'search',
      updated_at: Number(parsed.updated_at) || Date.now(),
    };
  } catch {
    return null;
  }
}

export function writeDiscoveryMatchingState(state: DiscoveryMatchingState): void {
  if (!canUseStorage()) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export function beginDiscoverySearch(params: {
  proIdsInRankOrder: number[];
  resultCount: number;
  mode: 'search' | 'ask';
}): DiscoveryMatchingState {
  const top5 = params.proIdsInRankOrder
    .map(Number)
    .filter(n => Number.isFinite(n) && n > 0)
    .slice(0, 5);
  const state: DiscoveryMatchingState = {
    search_id: newSearchId(),
    top5_pro_ids: top5,
    result_count: Math.max(0, Math.floor(params.resultCount)),
    mode: params.mode,
    updated_at: Date.now(),
  };
  writeDiscoveryMatchingState(state);
  return state;
}

export function discoveryEntryForBooking(): DiscoveryMatchingEntry {
  const state = readDiscoveryMatchingState();
  if (!state?.search_id) return 'direct';
  return state.mode === 'ask' ? 'ask' : 'discovery';
}

export function discoveryMatchPropsForPro(proId: number | null | undefined): {
  search_id: string | null;
  entry: DiscoveryMatchingEntry;
  discovery_match_top5: boolean | null;
} {
  const state = readDiscoveryMatchingState();
  if (!state?.search_id) {
    return { search_id: null, entry: 'direct', discovery_match_top5: null };
  }
  const pid = Number(proId);
  const inTop5 =
    Number.isFinite(pid) && pid > 0 ? state.top5_pro_ids.includes(pid) : false;
  return {
    search_id: state.search_id,
    entry: state.mode === 'ask' ? 'ask' : 'discovery',
    discovery_match_top5: inTop5,
  };
}

/** Props à merger dans booking_creation_* / pro_email_contact_sent. */
export function discoveryMatchingBookingProps(proId: number | null | undefined): Record<
  string,
  string | boolean | null
> {
  const m = discoveryMatchPropsForPro(proId);
  return {
    search_id: m.search_id,
    entry: m.entry,
    discovery_match_top5: m.discovery_match_top5,
  };
}
